/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Grid } from "@material-ui/core";

import { progressCircles } from "components/Form/FormField";
import { PostEntry, PostEntryType } from "./PostEntry";

import {
  addPost,
  deletePost,
  getCoursePosts,
  updatePosts,
  ApricotAction,
} from "client/actions/post";

import postsStyle from "assets/jss/views/coursePage/postsStyle";
const styles = createStyles(postsStyle as any);

export type PostActionType = {
  action: "pz_new" | "pz_add" | "pz_chg" | "pz_quit" | "pz_purge";
  data?: any;
};

interface Props extends WithStyles<typeof styles> {
  addPost: ApricotAction;
  deletePost: ApricotAction;
  getCoursePosts: ApricotAction;
  updatePosts: ApricotAction;
  posts: PostEntryType[];
  pCourse: string;
  user: any;
  classes: any;
  // component parameters:
  cid: string;
}

const PostsZoneBase: React.FC<Props> = ({
  addPost,
  deletePost,
  getCoursePosts,
  updatePosts,
  posts,
  pCourse,
  user,
  classes,
  cid,
}: Props) => {
  const [newPost, setNewPost] = useState(false);

  const refChanges = useRef({});

  useEffect(() => {
    // use 'x' as uid, if not logged on
    getCoursePosts(cid, user ? user._id : "x");

    return () => {
      if (Object.keys(refChanges.current).length) {
        updatePosts(refChanges.current);
      }
    };
  }, []);

  const onPostAction = (params: PostActionType) => {
    switch (params.action) {
      case "pz_new":
        setNewPost(true);
        break;

      case "pz_add": {
        const { content } = params.data;
        const { _id, picture, name } = user;
        addPost({ user: _id, course: cid, content, name, picture });
        setNewPost(false);
        break;
      }

      case "pz_quit":
        setNewPost(false);
        break;

      case "pz_purge":
        deletePost(params.data._id);
        break;

      case "pz_chg": {
        // received a change, merge into the entry of the changes list
        const pid = Object.keys(params.data)[0];
        refChanges.current[pid] === undefined
          ? (refChanges.current[pid] = params.data[pid])
          : Object.assign(refChanges.current[pid], params.data[pid]);
      }
    }
  };

  const renderPosts = () => {
    const postList =
      posts && pCourse === cid
        ? posts.map((post) => {
            return (
              <Grid item xs={12} sm={12} md={6} lg={4} key={post._id}>
                <PostEntry
                  state={"static"}
                  post={post}
                  entryAction={(params: PostActionType) => onPostAction(params)}
                />
              </Grid>
            );
          })
        : progressCircles();

    return (
      <Grid container>
        <Grid item xs={12} sm={12} md={6} lg={4}>
          <PostEntry
            post={{}}
            state={newPost ? "edit" : "ready"}
            entryAction={(params: PostActionType) => onPostAction(params)}
          />
        </Grid>
        {postList}
      </Grid>
    );
  };

  return <div className={classes.postsBoard}>{renderPosts()}</div>;
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
    posts: state.state_posts.posts,
    pCourse: state.state_posts.course,
  };
}

const base: any = PostsZoneBase;
export const PostsZone: any = withStyles(styles)(
  connect(mapStateToProps, {
    addPost,
    deletePost,
    getCoursePosts,
    updatePosts,
  })(base),
);

export const PostsZoneTest = withStyles(styles)(PostsZoneBase);
